import React from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Plus, User, FileText, Shield, Edit } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { useTeamMembers } from '../../../hooks/useTeamMembers';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import { cn } from '../../../../../lib/utils';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { TeamMemberFilters } from './TeamMemberFilters';

interface ProjectTeamProps {
  project: DashboardProject;
}

const ROLE_ICONS = {
  'Supervisor': Shield,
  'Welder': User,
  'Inspector': FileText,
} as const;

export const ProjectTeam: React.FC<ProjectTeamProps> = ({ project }) => {
  const {
    filteredTeamMembers,
    selectedTeamMember,
    isAddingTeamMember,
    filters,
    hasActiveFilters,
    setSelectedTeamMember,
    setIsAddingTeamMember,
    setFilters,
    handleTeamMemberDocumentUpload,
    handleSaveTeamMember,
    handleEditTeamMember,
    handleViewTeamMemberDocuments,
    handleAddNewTeamMember
  } = useTeamMembers();

  // Add state for roles and certifications
  const [roles, setRoles] = React.useState([
    'QC Inspector',
    'Welder',
    'Engineer',
    'Supervisor',
    'Customer Representative'
  ]);
  const [certifications, setCertifications] = React.useState([
    { label: 'Welding Level 1', value: 'welding-1' },
    { label: 'Welding Level 2', value: 'welding-2' },
    { label: 'NDT Level 1', value: 'ndt-1' },
    { label: 'NDT Level 2', value: 'ndt-2' },
    { label: 'Safety Certification', value: 'safety' }
  ]);

  // Add handlers for new items
  const [newRole, setNewRole] = React.useState('');
  const [newCertification, setNewCertification] = React.useState('');
  const [isAddingRole, setIsAddingRole] = React.useState(false);
  const [isAddingCertification, setIsAddingCertification] = React.useState(false);

  const handleAddRole = () => {
    if (newRole.trim()) {
      setRoles([...roles, newRole.trim()]);
      setNewRole('');
      setIsAddingRole(false);
    }
  };

  const handleAddCertification = () => {
    if (newCertification.trim()) {
      const newValue = newCertification.toLowerCase().replace(/\s+/g, '-');
      setCertifications([
        ...certifications,
        { label: newCertification.trim(), value: newValue }
      ]);
      setNewCertification('');
      setIsAddingCertification(false);
    }
  };

  return (
    <Card className="border-none shadow-none">
      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Team Members</h2>
            <AddTeamMemberModal
              isOpen={isAddingTeamMember}
              onOpenChange={setIsAddingTeamMember}
              selectedTeamMember={selectedTeamMember}
              setSelectedTeamMember={setSelectedTeamMember}
              handleTeamMemberDocumentUpload={handleTeamMemberDocumentUpload}
              handleSaveTeamMember={handleSaveTeamMember}
              roles={roles}
              certifications={certifications}
              isAddingRole={isAddingRole}
              setIsAddingRole={setIsAddingRole}
              isAddingCertification={isAddingCertification}
              setIsAddingCertification={setIsAddingCertification}
              newRole={newRole}
              setNewRole={setNewRole}
              newCertification={newCertification}
              setNewCertification={setNewCertification}
              handleAddRole={handleAddRole}
              handleAddCertification={handleAddCertification}
            />
          </div>

          <TeamMemberFilters
            filters={filters}
            onFiltersChange={setFilters}
            roles={roles}
            hasActiveFilters={hasActiveFilters}
          />

          <Card>
            <CardContent className="p-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTeamMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{member.role}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.phone}</TableCell>
                      <TableCell>
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium",
                          member.status === 'active' ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        )}>
                          {member.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                            onClick={() => handleEditTeamMember(member)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                            onClick={() => handleViewTeamMemberDocuments(member)}
                          >
                            <FileText className="h-4 w-4" />
                            {member.documents?.length || 0}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
}; 